// adaptNotification.js — convertit une notification brute de l'API
// (/api/notifications) vers le view-model attendu par <NotificationsMobile>.
//
// Forme brute (cf. routes/notifications.js + NotificationBell.tsx) :
//   { id, type, title, message, data (objet | string JSON | null), priority,
//     created_at, is_read, is_dismissed, warehouse_name }
//
// Le view-model garantit toujours : meta:[], detail:[], cta:{ic,label,to?}, msg.
import { fmt } from "./notificationsShared";
import { getDailyRecapRows } from "../../utils/dailyRecapDisplay";
import { getDailyRecapReportAction } from "../../utils/dailyRecapReportLink";

// Groupage fr-FR sans suffixe (quantités, seuils…).
const num = (v) => new Intl.NumberFormat("fr-FR").format(Number(v) || 0);

// ── type API → type view-model (5 familles) ─────────────────────────────────
const TYPE_MAP = {
  ecommerce_new_order: "commande",
  ecommerce_status_change: "commande",
  ecommerce_cancellation: "commande",
  order_pending: "commande",
  restaurant_new_order: "commande",
  payment_due: "paiement",
  subscription_billing_alert: "paiement",
  expense_alert: "paiement",
  expense_supplier_missing: "paiement",
  expense_due_alert: "paiement",
  payables_regularization_alert: "paiement",
  sales_receivables_alert: "paiement",
  stock_alert: "stock",
  transfer_received: "stock",
  transfer_confirmed: "stock",
  production_alert: "stock",
  stock_adjustment: "stock",
  stock_supply_practice_alert: "stock",
  production_completed: "stock",
  sale_completed: "commande",
  purchase_received: "commande",
  daily_recap: "systeme",
  planning_event: "tache",
  user_action: "systeme",
  new_customer: "systeme",
  system_maintenance: "systeme",
  system_update: "systeme",
  report_ready: "systeme",
};

// ── type API → route de destination du CTA (chemins vérifiés existants) ──────
const ROUTE_MAP = {
  ecommerce_new_order: "/gestion-ecommerce/commandes-en-ligne",
  ecommerce_status_change: "/gestion-ecommerce/commandes-en-ligne",
  ecommerce_cancellation: "/gestion-ecommerce/commandes-en-ligne",
  restaurant_new_order: "/gestion-ventes/commandes-restaurant",
  order_pending: "/gestion-ventes/ventes",
  payment_due: "/gestion-ventes/paiements-entrants",
  subscription_billing_alert: "/souscription",
  expense_alert: "/gestion-achats/paiements-sortants",
  expense_supplier_missing: "/gestion-charges-admin/saisie",
  expense_due_alert: "/gestion-charges-admin/saisie",
  payables_regularization_alert: "/gestion-achats/paiements-sortants",
  sales_receivables_alert: "/gestion-ventes/paiements-entrants",
  stock_alert: "/gestion-stock",
  transfer_received: "/gestion-stock",
  transfer_confirmed: "/gestion-stock",
  production_alert: "/gestion-stock",
  stock_adjustment: "/gestion-stock",
  stock_supply_practice_alert: "/gestion-stock",
  production_completed: "/production/history",
  sale_completed: "/gestion-ventes/ventes",
  purchase_received: "/gestion-achats/achat",
  daily_recap: "/rapport",
  planning_event: "/rh/planning",
};

const CTA_LABEL = {
  commande: "Voir la commande",
  paiement: "Voir le paiement",
  stock: "Voir le stock",
  tache: "Examiner",
  systeme: "Ouvrir",
};

// Parse défensif du champ `data` (objet | string JSON | null).
function parseData(data) {
  if (!data) return {};
  if (typeof data === "object") return data;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function dailyRecapOrganization(data, warehouseName) {
  const company = String(data.company_name || "").trim();
  const warehouse = String(data.warehouse_name || warehouseName || "").trim();
  const normalize = (value) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .toLocaleLowerCase("fr-FR");

  if (company && warehouse && normalize(company) !== normalize(warehouse)) {
    return `${company} · ${warehouse}`;
  }
  return company || warehouse || "ELSA GESTION";
}

// Libellé relatif ("Il y a 51min") — aligné sur NotificationBell.formatRelativeTime.
export function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin}min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Il y a ${diffH}h`;
  const diffJ = Math.floor(diffH / 24);
  if (diffJ < 7) return `Il y a ${diffJ}j`;
  return date.toLocaleDateString("fr-FR");
}

// Regroupement par jour calendaire : today / yesterday / earlier.
function bucketOf(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "earlier";
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(date)) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return "earlier";
}

function absoluteDateTime(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
}

// Détail générique (types sans rendu dédié) : champs scalaires de `data`.
function genericDetail(data) {
  return Object.entries(data)
    .filter(([, v]) => v !== null && typeof v !== "object")
    .slice(0, 6)
    .map(([k, v]) => ({ ic: "info", k, v: String(v) }));
}

// Construit meta (carte) + detail (modal) selon le type API.
function buildContent(type, data) {
  const meta = [];
  const detail = [];

  switch (type) {
    case "planning_event": {
      if (data.task_title) detail.push({ ic: "clipboard-check", k: "Tâche", v: data.task_title });
      break;
    }
    case "ecommerce_new_order":
    case "restaurant_new_order": {
      const articles =
        type === "restaurant_new_order"
          ? `${num(data.items_count)} article(s)`
          : `${num(data.items_count)} produit(s) · ${num(data.total_quantity)} unité(s)`;
      const client = data.customer_name || "Client comptoir";
      if (data.items_count != null) meta.push({ ic: "package", t: articles });
      meta.push({ ic: "user", t: client });
      if (data.store_name || data.source_label || data.table_label)
        meta.push({ ic: "store", t: data.store_name || data.source_label || data.table_label });

      if (data.order_number) detail.push({ ic: "hash", k: "Commande", v: data.order_number });
      if (data.store_name) detail.push({ ic: "store", k: "Boutique", v: data.store_name });
      if (data.source_label || data.table_label || data.service_label)
        detail.push({ ic: "store", k: "Service", v: data.table_label || data.service_label || data.source_label });
      if (data.items_count != null) detail.push({ ic: "package", k: "Articles", v: articles });
      detail.push({ ic: "user", k: "Client", v: client });
      if (data.customer_phone) detail.push({ ic: "smartphone", k: "Téléphone", v: data.customer_phone });
      break;
    }
    case "ecommerce_status_change": {
      if (data.order_number) meta.push({ ic: "hash", t: data.order_number });
      if (data.customer_name) meta.push({ ic: "user", t: data.customer_name });
      if (data.order_number) detail.push({ ic: "hash", k: "Commande", v: data.order_number });
      if (data.store_name) detail.push({ ic: "store", k: "Boutique", v: data.store_name });
      if (data.old_status || data.new_status)
        detail.push({ ic: "refresh-ccw", k: "Statut", v: `${data.old_status || "?"} → ${data.new_status || "?"}` });
      if (data.customer_name) detail.push({ ic: "user", k: "Client", v: data.customer_name });
      break;
    }
    case "ecommerce_cancellation": {
      if (data.order_number) meta.push({ ic: "hash", t: data.order_number });
      if (data.customer_name) meta.push({ ic: "user", t: data.customer_name });
      if (data.order_number) detail.push({ ic: "hash", k: "Commande", v: data.order_number });
      if (data.store_name) detail.push({ ic: "store", k: "Boutique", v: data.store_name });
      if (data.cancellation_reason) detail.push({ ic: "x-circle", k: "Motif", v: data.cancellation_reason });
      if (data.customer_name) detail.push({ ic: "user", k: "Client", v: data.customer_name });
      break;
    }
    case "stock_alert": {
      if (data.current_stock != null)
        meta.push({ ic: "layers", t: `Reste ${num(data.current_stock)}`, strong: true });
      if (data.stock_threshold != null) meta.push({ ic: "bell", t: `Seuil : ${num(data.stock_threshold)}` });
      if (data.product_name) detail.push({ ic: "box", k: "Produit", v: data.product_name });
      if (data.current_stock != null) detail.push({ ic: "layers", k: "Stock restant", v: `${num(data.current_stock)} unité(s)` });
      if (data.stock_threshold != null) detail.push({ ic: "bell", k: "Seuil d'alerte", v: `${num(data.stock_threshold)} unité(s)` });
      break;
    }
    case "transfer_received":
    case "transfer_confirmed": {
      const articles = `${num(data.total_items)} produit(s) · ${num(data.total_quantity)} unité(s)`;
      if (data.total_items != null) meta.push({ ic: "package", t: articles });
      if (type === "transfer_received" && data.from_warehouse_name)
        meta.push({ ic: "map-pin", t: `De : ${data.from_warehouse_name}` });
      if (type === "transfer_confirmed" && data.to_warehouse_name)
        meta.push({ ic: "map-pin", t: `Vers : ${data.to_warehouse_name}` });

      if (data.transfer_number) detail.push({ ic: "hash", k: "Transfert", v: data.transfer_number });
      if (data.from_warehouse_name) detail.push({ ic: "map-pin", k: "Source", v: data.from_warehouse_name });
      if (data.to_warehouse_name) detail.push({ ic: "map-pin", k: "Destination", v: data.to_warehouse_name });
      if (data.total_items != null) detail.push({ ic: "package", k: "Articles", v: articles });
      break;
    }
    case "daily_recap": {
      const recapRows = getDailyRecapRows(data);
      if (data.company_name) {
        detail.push({ ic: "store", k: "Entreprise", v: data.company_name });
      }
      if (data.warehouse_name) {
        detail.push({ ic: "map-pin", k: "Établissement", v: data.warehouse_name });
      }
      detail.push(
        ...recapRows.map((row) => ({
          ic: row.key.endsWith("_cost")
            ? "wallet"
            : row.key.includes("count") || row.key.includes("items")
            ? "package"
            : "wallet",
          k: row.label,
          v: row.value,
          amount: row.key === "net_cash_flow" || row.key.endsWith("_cost"),
        }))
      );
      break;
    }
    case "stock_supply_practice_alert": {
      meta.push({
        ic: "refresh-ccw",
        t: `${num(data.positive_adjustment_count)} ajustement(s) à la hausse`,
        strong: true,
      });
      meta.push({
        ic: "layers",
        t: `${num(data.manual_inbound_share_percent)} % d’entrées manuelles`,
      });

      if (data.company_name) {
        detail.push({ ic: "store", k: "Entreprise", v: data.company_name });
      }
      detail.push(
        { ic: "clock", k: "Période analysée", v: `${num(data.lookback_days)} jours` },
        { ic: "refresh-ccw", k: "Ajustements à la hausse", v: num(data.positive_adjustment_count) },
        { ic: "clock", k: "Jours avec ajustement", v: num(data.distinct_adjustment_days) },
        { ic: "package", k: "Produits concernés", v: num(data.distinct_product_count) },
        { ic: "package", k: "Achats enregistrés", v: num(data.purchase_count) },
        { ic: "package", k: "Productions enregistrées", v: num(data.production_count) },
        { ic: "layers", k: "Part des entrées manuelles", v: `${num(data.manual_inbound_share_percent)} %` }
      );
      break;
    }
    case "expense_supplier_missing": {
      meta.push({
        ic: "user",
        t: "Fournisseur à compléter",
        strong: true,
      });
      if (data.expense_amount_display) {
        meta.push({ ic: "wallet", t: data.expense_amount_display });
      }
      if (data.expense_reference) {
        detail.push({ ic: "hash", k: "Dépense", v: data.expense_reference });
      }
      if (data.expense_category) {
        detail.push({ ic: "package", k: "Catégorie", v: data.expense_category });
      }
      if (data.expense_amount_display) {
        detail.push({ ic: "wallet", k: "Montant", v: data.expense_amount_display, amount: true });
      }
      break;
    }
    case "expense_due_alert": {
      meta.push({
        ic: "clock",
        t: `${num(data.due_today_count)} dépense(s) à échéance`,
        strong: true,
      });
      if (data.due_today_amount_display) {
        meta.push({ ic: "wallet", t: data.due_today_amount_display });
      }
      detail.push({
        ic: "wallet",
        k: "Solde à régler aujourd’hui",
        v: data.due_today_amount_display || "Montant indisponible",
        amount: true,
      });
      if (data.due_today_missing_supplier_count) {
        detail.push({
          ic: "user",
          k: "Sans fournisseur",
          v: num(data.due_today_missing_supplier_count),
        });
      }
      break;
    }
    case "payables_regularization_alert": {
      const issueCount =
        Number(data.expense_missing_supplier_count || 0) +
        Number(data.purchase_missing_supplier_count || 0) +
        Number(data.old_expense_count || 0) +
        Number(data.overdue_expense_count || 0) +
        Number(data.old_purchase_count || 0);
      meta.push({
        ic: "wallet",
        t: `${num(issueCount)} pièce(s) à vérifier`,
        strong: true,
      });
      if (data.expense_missing_supplier_count) {
        detail.push({
          ic: "user",
          k: "Dépenses sans fournisseur",
          v: `${num(data.expense_missing_supplier_count)} · ${data.expense_missing_supplier_amount_display}`,
        });
      }
      if (data.purchase_missing_supplier_count) {
        detail.push({
          ic: "user",
          k: "Achats sans fournisseur",
          v: `${num(data.purchase_missing_supplier_count)} · ${data.purchase_missing_supplier_amount_display}`,
        });
      }
      if (data.old_expense_count) {
        detail.push({
          ic: "clock",
          k: `Dépenses de plus de ${num(data.expense_age_days)} jours`,
          v: `${num(data.old_expense_count)} · ${data.old_expense_amount_display}`,
        });
      }
      if (data.overdue_expense_count) {
        detail.push({
          ic: "clock",
          k: "Dépenses après échéance",
          v: `${num(data.overdue_expense_count)} · ${data.overdue_expense_amount_display}`,
        });
      }
      if (data.old_purchase_count) {
        detail.push({
          ic: "clock",
          k: `Achats de plus de ${num(data.purchase_age_days)} jours`,
          v: `${num(data.old_purchase_count)} · ${data.old_purchase_amount_display}`,
        });
      }
      break;
    }
    case "sales_receivables_alert": {
      meta.push({
        ic: "wallet",
        t: `${num(data.old_sales_count)} facture(s) à encaisser`,
        strong: true,
      });
      if (data.old_sales_amount_display) {
        meta.push({ ic: "wallet", t: data.old_sales_amount_display });
      }
      detail.push({
        ic: "clock",
        k: `Ventes de plus de ${num(data.sales_age_days)} jours`,
        v: `${num(data.old_sales_count)} · ${data.old_sales_amount_display || "Montant indisponible"}`,
      });
      if (data.receivables_customer_count) {
        detail.push({
          ic: "user",
          k: "Clients concernés",
          v: num(data.receivables_customer_count),
        });
      }
      if (data.oldest_sale_age_days) {
        detail.push({
          ic: "clock",
          k: "Facture la plus ancienne",
          v: `${num(data.oldest_sale_age_days)} jours`,
        });
      }
      break;
    }
    default: {
      detail.push(...genericDetail(data));
    }
  }

  return { meta, detail };
}

export default function adaptNotification(raw) {
  const data = parseData(raw.data);
  const vmType = TYPE_MAP[raw.type] || "systeme";
  const { meta, detail } = buildContent(raw.type, data);

  // Montant mis en avant si disponible.
  const amount =
    data.total_amount != null && Number(data.total_amount) > 0 ? fmt(data.total_amount) : undefined;
  if (amount) detail.push({ ic: "wallet", k: "Montant", v: amount, amount: true });

  // Toujours une ligne de date absolue à la fin du détail.
  const when = absoluteDateTime(raw.created_at);
  if (when) detail.push({ ic: "clock", k: "Reçue", v: when });

  const dailyRecapAction =
    raw.type === "daily_recap"
      ? getDailyRecapReportAction(data, raw.warehouse_id)
      : null;
  const to = dailyRecapAction
    ? dailyRecapAction.url
    : typeof data.url === "string" && data.url.startsWith("/")
    ? data.url
    : ROUTE_MAP[raw.type];
  const ctaLabel =
    dailyRecapAction
      ? dailyRecapAction.label
      : raw.type === "expense_supplier_missing"
      ? "Compléter la dépense"
      : raw.type === "expense_due_alert"
      ? "Voir les dépenses à régler"
      : raw.type === "payables_regularization_alert"
      ? "Régulariser les pièces"
      : raw.type === "sales_receivables_alert"
      ? "Enregistrer les encaissements"
      : raw.type === "stock_supply_practice_alert"
      ? "Ouvrir le module conseillé"
      : raw.type === "planning_event"
      ? "Ouvrir la tâche"
      : raw.type === "transfer_received" || raw.type === "transfer_confirmed"
      ? "Voir le transfert"
      : CTA_LABEL[vmType];

  return {
    id: raw.id,
    type: vmType,
    sourceType: raw.type,
    read: Boolean(raw.is_read),
    bucket: bucketOf(raw.created_at),
    time: formatRelativeTime(raw.created_at),
    title: raw.title || "Notification",
    org:
      raw.type === "daily_recap"
        ? dailyRecapOrganization(data, raw.warehouse_name)
        : raw.warehouse_name || "ELSA GESTION",
    msg: raw.message || "",
    data,
    createdAt: raw.created_at,
    warehouseName: raw.warehouse_name || data.warehouse_name || "",
    meta,
    amount,
    detail,
    cta: { ic: "arrow-up-right", label: ctaLabel, to },
  };
}
